import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { useSubscription } from '../../hooks/useSubscription';

import { useIsCheckingAuth } from '../../contexts/AuthContext';
import { useSite } from '../../contexts/SiteContext';
import SubscriptionForm from '../forms/SubscriptionForm';
import Summary from './Summary';

import styles from '../../styles/summaries/micromag-summary.module.css';

type MicromagSummaryProps = {
    slug?: string | null;
    title?: string | null;
    description?: string | null;
    subscribeTitle?: string | null;
    subscribeDescription?: string | null;
    image?: Record<string, unknown> | null;
    metadata?: {
        sponsors?: Array<Record<string, unknown>> | null;
    } | null;
    source?: string | null;
    className?: string | null;
    imageClassName?: string | null;
    pictureClassName?: string | null;
    buttonClassName?: string | null;
} & Record<string, unknown>;

function MicromagSummary({
    slug = null,
    title = 'Vous êtes abonné·e au Micromag',
    description = null,
    subscribeTitle = 'Abonnez-vous au Micromag',
    subscribeDescription = 'Découvrez notre nouveau format de magazine gratuit en format numérique. Vous pouvez y consulter du contenu exclusif, nos suggestions de la semaine et même votre horoscope.',
    image = null,
    source = null,
    metadata = null,
    className = null,
    imageClassName = null,
    pictureClassName = null,
    buttonClassName = null,
    ...props
}: MicromagSummaryProps) {
    const { handle: siteHandle } = useSite();
    const { sponsors = null } = metadata || {};
    const isCheckingAuth = useIsCheckingAuth();
    const [sponsor = null] = sponsors || [];
    const url = useUrlGenerator();
    const { subscribed } = useSubscription({
        subscription: siteHandle === 'france' ? 'micromag_france' : 'micromag',
        source: source || 'micromag_summary',
    });

    return (
        <Summary
            surtitle={
                subscribed ? (siteHandle === 'france' ? 'Ce mois-ci' : 'Cette semaine') : null
            }
            title={!subscribed ? subscribeTitle || title : title || subscribeTitle}
            description={
                !subscribed
                    ? subscribeDescription || description
                    : description || subscribeDescription
            }
            sponsor={sponsor}
            image={image}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            imageClassName={classNames([
                styles.image,
                {
                    [imageClassName]: imageClassName !== null,
                },
            ])}
            pictureClassName={classNames([
                styles.picture,
                {
                    [pictureClassName]: pictureClassName !== null,
                },
            ])}
            buttonClassName={classNames([
                styles.button,
                {
                    [buttonClassName]: buttonClassName !== null,
                },
            ])}
            buttonLabel={
                <FormattedMessage
                    defaultMessage="Consultez le dernier micromag"
                    description="Button label"
                />
            }
            withButton={subscribed}
            withLink={false}
            link={
                slug !== null
                    ? url('micromag', {
                          slug,
                      })
                    : null
            }
            {...props}
        >
            {!subscribed && !isCheckingAuth ? (
                <SubscriptionForm
                    subscription={siteHandle === 'france' ? 'micromag_france' : 'micromag'}
                    source="micromag_featured"
                    className={styles.form}
                />
            ) : null}
        </Summary>
    );
}

export default MicromagSummary;
